import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { BadgeCheck, Check, Loader2, Printer, X } from "lucide-react";
import { base_url } from "../../config";

type BadgeState = {
  id: number;
  code: string;
  name: string;
  name_badge: string | null;
  affiliation_badge: string | null;
  badge_printed: boolean;
  bulk_print_requested: boolean;
  picked_badge: boolean;
  picked_badge_at: string | null;
};

/**
 * The badge controls for one registration, as a button plus a panel.
 *
 * Dropped into every admin modal that shows a registration. It takes only the
 * registration code and loads its own state, because the modals it lives in carry wildly
 * different payloads — the registration detail has the whole row, while the student, visa
 * and EurAI modals have little more than a code.
 *
 * A panel rather than a nested modal: these all open inside a modal already, and stacking
 * two of them makes the close buttons ambiguous.
 */
const BadgeButton = ({
  code,
  onChange,
  align = "right",
}: {
  code?: string | null;
  /** Called after the printed flag changes, so a parent list can refresh. */
  onChange?: (printed: boolean) => void;
  align?: "left" | "right";
}) => {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<BadgeState | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState("");
  const wrapper = useRef<HTMLDivElement>(null);

  const load = async () => {
    if (!code) return;
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${base_url}/api/admin/badge_state`, {
        params: { code },
      });
      setState(response.data);
    } catch (e) {
      setError("Could not load the badge state.");
    } finally {
      setLoading(false);
    }
  };

  // Loaded on open rather than on mount: most of the time the panel is never opened, and
  // these components render once per modal.
  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, code]);

  // Click-outside, so the panel behaves like a menu inside the modal that hosts it.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (event: MouseEvent) => {
      if (wrapper.current && !wrapper.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const togglePrinted = async () => {
    if (!state || busy) return;
    setBusy(true);
    setError("");
    const next = !state.badge_printed;
    try {
      const response = await axios.post(`${base_url}/api/admin/update_badge_printed`, {
        code: state.code,
        value: next,
      });
      const printed = !!response.data.badge_printed;
      setState({ ...state, badge_printed: printed });
      onChange?.(printed);
    } catch (e) {
      setError("Could not change the printed flag.");
    } finally {
      setBusy(false);
    }
  };

  /** Print does not touch the printed flag — a reprint must not depend on it. */
  const print = async () => {
    if (!state || printing) return;
    setPrinting(true);
    setError("");
    try {
      const response = await axios.post(
        `${base_url}/api/admin/print_badge`,
        { code: state.code },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `badge_${state.code}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError("Could not build the badge PDF.");
    } finally {
      setPrinting(false);
    }
  };

  if (!code) return null;

  return (
    <div className="relative inline-block" ref={wrapper}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-slate-700/40 text-slate-200 border border-slate-600/40 hover:bg-slate-700/70 transition-colors"
      >
        <BadgeCheck className="h-4 w-4" />
        Badge
      </button>

      {open && (
        <div
          className={`absolute z-[60] mt-1 w-72 rounded-xl border border-slate-700/60 bg-slate-900 shadow-2xl p-3 space-y-3 ${
            align === "right" ? "right-0" : "left-0"
          }`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-xs font-semibold text-slate-100">Badge</div>
              <div className="text-[11px] text-slate-500 font-mono truncate">{code}</div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-slate-500 hover:text-slate-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-xs text-slate-500 py-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
            </div>
          ) : state ? (
            <>
              <div className="text-xs text-slate-200">
                <div className="truncate">{state.name_badge || state.name}</div>
                {state.affiliation_badge && (
                  <div className="text-[11px] text-slate-500 truncate">
                    {state.affiliation_badge}
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={togglePrinted}
                disabled={busy}
                className={`w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg border text-xs transition-colors disabled:opacity-50 ${
                  state.badge_printed
                    ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/25"
                    : "bg-slate-800/60 border-slate-600/50 text-slate-300 hover:bg-slate-700/60"
                }`}
              >
                <span className="font-medium">
                  {state.badge_printed ? "Printed" : "Not printed"}
                </span>
                <span className="inline-flex items-center gap-1 text-[11px]">
                  {busy ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : state.badge_printed ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : null}
                  {busy ? "" : state.badge_printed ? "mark as not printed" : "mark as printed"}
                </span>
              </button>

              {(state.picked_badge || state.bulk_print_requested) && (
                <div className="text-[11px] text-slate-500 space-y-0.5">
                  {state.picked_badge && (
                    <div>
                      Collected at the desk
                      {state.picked_badge_at ? ` · ${state.picked_badge_at}` : ""}
                    </div>
                  )}
                  {state.bulk_print_requested && <div>Queued for bulk printing</div>}
                </div>
              )}

              <button
                type="button"
                onClick={print}
                disabled={printing}
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-blue-500/25 text-blue-100 border border-blue-500/40 hover:bg-blue-500/35 transition-colors disabled:opacity-50"
              >
                {printing ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Printer className="h-3.5 w-3.5" />
                )}
                {state.badge_printed ? "Print again" : "Print badge"}
              </button>

              <p className="text-[10px] text-slate-500 leading-snug">
                Printing does not change the flag — reprinting a lost badge must not depend
                on it.
              </p>
            </>
          ) : null}

          {error && <div className="text-[11px] text-red-300">{error}</div>}
        </div>
      )}
    </div>
  );
};

export default BadgeButton;
